import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconUser, IconPhone, IconMail, IconCake, IconCheck, IconX, IconStar,
  IconArrowRight, IconHandClick, IconGift, IconConfetti,
  type Icon as TablerIcon,
} from '@tabler/icons-react';

/**
 * CustomerOnboardingPage — full-screen "flip the iPad" loyalty signup.
 *
 * Cashier taps "Pass to customer" in the CustomerPopover → navigates
 * here. Customer fills three fields with the iPad facing them.
 * On submit:
 *   1. Brief celebration screen ("Welcome, {name}!")
 *   2. Tap to hand back → drop the new customer object in
 *      sessionStorage and navigate back to /pos.
 *   3. PosPage on focus reads sessionStorage and auto-attaches the
 *      customer to the open order.
 *
 * Design goals:
 *   - Premium feel — the customer should think "wow, this is nice"
 *   - Big touch targets (h-14 inputs, h-14 CTA) — they're standing up
 *     across a counter, not at a laptop
 *   - Live preview card — types appear on the visual immediately so
 *     the customer sees the value of giving their details
 */

const CARD_LIVE_KEY = 'pos.pending-customer-attach';

function initialsOf(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

function formatPhone(raw: string) {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

function formatMemberForCard(raw: string) {
  // The DB stores something like "HL00012345". For the card, group
  // for readability: "HL 0001 2345".
  if (!raw) return '';
  const prefix = raw.replace(/[0-9]/g, '');
  const digits = raw.replace(/[^0-9]/g, '');
  const grouped = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  return prefix ? `${prefix} ${grouped}`.trim() : grouped;
}

export default function CustomerOnboardingPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  // Real member number returned by the API on success. Pre-fill with a
  // placeholder so the card layout doesn't jump.
  const [memberNo, setMemberNo] = useState<string>('HL — — — —');
  const [memberSince] = useState(() =>
    new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }).toUpperCase()
  );
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdCustomer, setCreatedCustomer] = useState<{
    id: string;
    name: string;
    phone: string;
    email?: string;
  } | null>(null);

  // Lock body scroll while the customer is using the iPad — they
  // shouldn't be able to swipe to anything else.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const canSubmit = name.trim().length >= 2 && phone.replace(/\D/g, '').length >= 9 && !submitting;

  const handleJoin = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await fetch('/api/customers', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.replace(/\D/g, ''),
          email: email.trim() || null,
          dob: dob || null,
        }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${r.status}`);
      }
      const data = await r.json();
      setMemberNo(formatMemberForCard(data.customer.memberNumber));
      setCreatedCustomer({
        id: data.customer.id,
        name: data.customer.name,
        phone: data.customer.phone,
        email: data.customer.email ?? undefined,
      });
      setStep('success');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handBack = () => {
    if (createdCustomer) {
      const customer = {
        id: createdCustomer.id,
        name: createdCustomer.name,
        phone: createdCustomer.phone,
        email: createdCustomer.email,
        loyaltyPoints: 0,
        lastVisit: 'just now',
        totalSpentCents: 0,
      };
      sessionStorage.setItem(CARD_LIVE_KEY, JSON.stringify(customer));
    }
    navigate('/pos');
  };

  const cancel = () => navigate('/pos');

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-gradient-to-br from-[#1a0303] via-[#3d0808] to-[#7a1010] text-[#FFFFFF]">
      {/* Cashier-only escape hatch — small + low contrast so the
          customer doesn't accidentally tap it. */}
      <button
        onClick={cancel}
        className="absolute top-5 right-5 z-20 w-10 h-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-[#FFFFFF]/80 transition-colors"
        aria-label="Cancel signup"
      >
        <IconX size={18} stroke={2} />
      </button>

      {step === 'form' && (
        <div className="h-full grid grid-cols-1 lg:grid-cols-2 overflow-y-auto">
          {/* LEFT — animated membership card */}
          <div className="hidden lg:flex flex-col items-center justify-center p-12 relative">
            {/* Ambient glow */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(255,80,80,0.25),transparent_60%)] pointer-events-none" />

            <div className="relative z-10 w-full max-w-[460px]">
              <p className="text-12 font-bold uppercase tracking-[0.3em] text-[#FFFFFF]/60 mb-4 text-center">
                Your membership card
              </p>

              {/* THE CARD — physical card metaphor, ratio ~1.6:1 */}
              <div className="relative aspect-[1.6/1] rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#dd0000] via-[#a30606] to-[#5e0303] border border-white/20">
                {/* Subtle shimmer overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(115deg,transparent_30%,rgba(255,255,255,0.12)_45%,transparent_60%)] pointer-events-none" />
                {/* Embossed CODED mark in corner */}
                <img
                  src="/icons/icon-96.png"
                  alt=""
                  className="absolute top-5 right-5 w-10 h-10 rounded-md opacity-90 shadow-md"
                />
                <div className="relative h-full flex flex-col justify-between p-6">
                  {/* Header */}
                  <div>
                    <p className="font-cormorant italic text-22 text-[#FFFFFF]/95 leading-none">
                      HL Coffee Shop
                    </p>
                    <p className="text-11 font-bold uppercase tracking-[0.25em] text-[#FFFFFF]/65 mt-1">
                      Loyalty Member
                    </p>
                  </div>

                  {/* Centre — name + member no */}
                  <div className="space-y-2">
                    <p className="text-12 font-semibold uppercase tracking-widest text-[#FFFFFF]/55">
                      Member name
                    </p>
                    <p
                      key={name || 'placeholder'}
                      className={[
                        'text-26 leading-tight font-bold tabular-nums truncate transition-opacity duration-300 animate-fade-in',
                        name ? 'text-[#FFFFFF]' : 'text-[#FFFFFF]/30 italic',
                      ].join(' ')}
                    >
                      {name.trim() || 'Awaiting…'}
                    </p>
                  </div>

                  {/* Footer row */}
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-10 font-bold uppercase tracking-[0.25em] text-[#FFFFFF]/55 mb-1">
                        Member no.
                      </p>
                      <p className="text-13 font-bold tabular-nums tracking-wider text-[#FFFFFF]/90">
                        {memberNo}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-10 font-bold uppercase tracking-[0.25em] text-[#FFFFFF]/55 mb-1">
                        Member since
                      </p>
                      <p className="text-13 font-bold tabular-nums tracking-wider text-[#FFFFFF]/90">
                        {memberSince}
                      </p>
                    </div>
                    <span className="inline-flex items-center text-11 font-bold uppercase tracking-widest px-2.5 h-6 rounded bg-orange text-[#FFFFFF] shadow-sm">
                      Bronze
                    </span>
                  </div>
                </div>
              </div>

              {/* Benefits row under the card */}
              <div className="mt-8 grid grid-cols-3 gap-3">
                {[
                  { Icon: IconStar, label: '1 point per Rand' },
                  { Icon: IconGift, label: 'Birthday treat' },
                  { Icon: IconConfetti, label: 'Tier rewards' },
                ].map((b, i) => (
                  <div key={i} className="text-center">
                    <div className="w-11 h-11 mx-auto rounded-lg bg-white/10 backdrop-blur-sm inline-flex items-center justify-center text-[#FFFFFF]/85 mb-2">
                      <b.Icon size={20} stroke={2} />
                    </div>
                    <p className="text-12 font-semibold text-[#FFFFFF]/80 leading-tight">{b.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — welcome + form */}
          <div className="flex flex-col justify-center bg-white text-heading p-8 lg:p-12 lg:rounded-l-3xl shadow-2xl min-h-screen lg:min-h-0">
            <div className="w-full max-w-[460px] mx-auto">
              {/* Intro */}
              <p className="text-12 font-bold uppercase tracking-[0.25em] text-primary mb-3">
                Welcome to HL Coffee Shop
              </p>
              <h1 className="text-30 font-bold text-heading leading-tight">
                Join our loyalty<br />programme.
              </h1>
              <p className="text-15 font-medium text-gray-700 mt-3 leading-snug">
                Earn a point for every Rand you spend, get a free drink on your birthday,
                and unlock perks as you climb the tiers.
              </p>

              {/* Form */}
              <div className="space-y-5 mt-8">
                <Field
                  Icon={IconUser}
                  label="Your name"
                  required
                  value={name}
                  onChange={setName}
                  placeholder="e.g. Aaliyah Khan"
                  autoFocus
                />
                <Field
                  Icon={IconPhone}
                  label="Phone number"
                  required
                  value={formatPhone(phone)}
                  onChange={(v) => setPhone(v.replace(/\D/g, '').slice(0, 10))}
                  placeholder="082 123 4567"
                  type="tel"
                  className="tabular-nums"
                />
                <Field
                  Icon={IconMail}
                  label="Email"
                  helper="optional, for digital receipts"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.co.za"
                  type="email"
                />
                <Field
                  Icon={IconCake}
                  label="Date of birth"
                  helper="for your free birthday coffee (ID proof required at till)"
                  value={dob}
                  onChange={setDob}
                  placeholder="YYYY-MM-DD"
                  type="date"
                />
              </div>

              {/* CTA */}
              <button
                type="button"
                disabled={!canSubmit}
                onClick={handleJoin}
                className={[
                  'w-full h-14 mt-8 inline-flex items-center justify-center gap-2.5 rounded-xl text-16 font-bold transition-all',
                  canSubmit
                    ? 'bg-primary text-[#FFFFFF] hover:bg-primary-hover shadow-lg active:scale-[0.99]'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                ].join(' ')}
              >
                {submitting ? 'Saving…' : 'Join the loyalty programme'}
                <IconArrowRight size={20} stroke={2.5} />
              </button>

              {error && (
                <p className="text-13 font-semibold text-danger mt-4 text-center">{error}</p>
              )}

              <p className="text-12 text-gray-500 mt-4 text-center leading-snug">
                We'll only use your details for loyalty rewards and order receipts.
                <br className="hidden sm:block" />
                You can opt out any time.
              </p>
            </div>
          </div>
        </div>
      )}

      {step === 'success' && (
        <div className="h-full flex items-center justify-center p-8 animate-fade-in">
          <div className="text-center max-w-[520px] mx-auto">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-success inline-flex items-center justify-center shadow-2xl animate-slide-up">
              <IconCheck size={56} stroke={3} className="text-[#FFFFFF]" />
            </div>
            <p className="text-12 font-bold uppercase tracking-[0.3em] text-[#FFFFFF]/60 mb-3">
              You're in
            </p>
            <h2 className="text-40 font-bold leading-tight text-[#FFFFFF]">
              Welcome,<br />
              <span className="font-cormorant italic">{name.trim()}</span>
            </h2>
            <p className="text-16 text-[#FFFFFF]/80 mt-4 leading-snug">
              Your loyalty card has been activated.<br />
              You've earned <span className="font-bold text-[#FFFFFF]">10 welcome points</span> to start.
            </p>

            <button
              type="button"
              onClick={handBack}
              className="mt-10 h-14 px-8 inline-flex items-center justify-center gap-3 rounded-xl bg-white text-heading text-16 font-bold hover:bg-white/95 shadow-2xl active:scale-[0.99] transition-all"
            >
              <IconHandClick size={20} stroke={2.5} />
              Hand back to cashier
            </button>

            <p className="text-12 text-[#FFFFFF]/50 mt-5">
              Initials: <span className="font-bold tracking-widest text-[#FFFFFF]/70">{initialsOf(name)}</span>
              {' · '}Member no: <span className="font-bold tabular-nums text-[#FFFFFF]/70">{memberNo}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Field({
  Icon, label, helper, required, value, onChange, placeholder, type = 'text', className, autoFocus,
}: {
  Icon: TablerIcon;
  label: string;
  helper?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="block text-14 font-bold text-heading mb-2">
        {label}
        {required && <span className="text-danger"> *</span>}
        {helper && <span className="text-13 text-gray-600 font-semibold ml-1">— {helper}</span>}
      </label>
      <div className="relative">
        <Icon size={18} stroke={2} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type={type}
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={[
            'w-full h-14 pl-12 pr-4 rounded-xl bg-light border-2 border-border focus:border-primary text-15 font-medium text-heading placeholder-gray-400 outline-none transition-colors',
            className ?? '',
          ].join(' ')}
        />
      </div>
    </div>
  );
}
